import { Button, type ComboboxItem, Group } from "@mantine/core";
import * as _ from "lodash-es";
import { useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../mantine/notifications/useToast";
import type { OAuthApplicationScope } from "../../../types/api.types";
import { FormFieldController } from "../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { isFieldError } from "../../BuildingBlocks/Forms/MantineFormFields/HelperText/utils/isFieldError";
import { setErrorsFromResponseData } from "../../BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import type {
  OAuthApplicationCreate,
  OAuthApplicationCustomers
} from "../ApiSettings.types";
import {
  OAUTH_APPLICATION_FORM_FIELD_DATA,
  SCOPE_LABELS
} from "./OAuthApplicationForm.constants";

interface OAuthApplicationFormProps {
  buttonContainer?: HTMLElement;
  customers: OAuthApplicationCustomers;
  scopes: Array<OAuthApplicationScope>;
  onCancel: () => void;
  onSubmit: SubmitHandler<OAuthApplicationCreate>;
}

function OAuthApplicationForm({
  customers,
  scopes,
  onCancel,
  onSubmit
}: OAuthApplicationFormProps) {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
    watch
  } = useForm<OAuthApplicationCreate>();
  const { showErrorToast } = useToast();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const customerChoices = _.sortBy(
    Object.keys(customers).map<{ value: number; label: string }>(
      (customerId) => ({
        label: customers[customerId],
        value: Number(customerId)
      })
    ),
    ["displayName"]
  );

  const scopeChoices = _.sortBy(
    scopes.map<ComboboxItem>((scope) => ({
      label: SCOPE_LABELS[scope],
      value: scope
    })),
    ["label"]
  );

  const onValidSubmit: SubmitHandler<OAuthApplicationCreate> = async (data) => {
    try {
      const response = await onSubmit(data);
      return response;
    } catch (error) {
      setErrorsFromResponseData<OAuthApplicationCreate>(
        showErrorToast,
        error,
        watch(),
        setError,
        t("errors.UnknownError")
      );
    }
  };

  const submitCallback = handleSubmit(onValidSubmit);

  async function handleClickSubmit() {
    setIsSubmitting(true);

    try {
      await submitCallback();
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleClickCancel() {
    reset();
    onCancel();
  }

  return (
    <form
      className="OAuthApplicationForm"
      noValidate
      onSubmit={handleClickSubmit}
    >
      <FormFieldController
        control={control}
        data={OAUTH_APPLICATION_FORM_FIELD_DATA.name}
        error={errors.name}
      />
      <FormFieldController
        control={control}
        data={{
          ...OAUTH_APPLICATION_FORM_FIELD_DATA.customer,
          data: customerChoices
        }}
        error={errors.customer}
      />
      <FormFieldController
        control={control}
        data={{
          ...OAUTH_APPLICATION_FORM_FIELD_DATA.scope,
          data: scopeChoices
        }}
        error={isFieldError(errors.scope) ? errors.scope : undefined}
      />
      <Group gap="xs" justify="flex-end" my="lg">
        <Button variant="default" onClick={handleClickCancel}>
          Schließen
        </Button>
        <Button loading={isSubmitting} onClick={handleClickSubmit}>
          Registrieren
        </Button>
      </Group>
    </form>
  );
}

export { OAuthApplicationForm, OAuthApplicationFormProps };
